import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { BehaviorSubject, EMPTY, Observable } from 'rxjs';
import firebase from 'firebase/compat/app';
type DocumentReference<T> = firebase.firestore.DocumentReference<T>;

/**
 * Lo unico que `readReviews()` usa de un documento leido.
 *
 * Se declara estructuralmente en vez de pedir el `QueryDocumentSnapshot` del SDK por el
 * mismo motivo por el que el contrato declara sus tipos de Firestore en vez de
 * importarlos: pedir el tipo entero obliga a cada prueba a fabricar `exists`, `ref`,
 * `metadata`, `get` e `isEqual`, que este metodo no toca.
 */
interface ReviewSnapshot {
  id: string;
  data(): unknown;
}

/** Lo mismo para `readFoods()`, que ademas necesita saber si el documento existe. */
interface FoodSnapshot {
  id: string;
  data(): unknown;
}
import 'firebase/compat/firestore';
import { FirestoreRefs } from 'src/app/core/firestore-refs';
import { HttpClient } from '@angular/common/http';
import { get } from 'lodash-es';
import { log, warn } from 'src/app/core/log';
import { isAccountDisabledError, isSuspendedStatus } from '../core/account-status';
import { COLLECTIONS, IOrder, OrderStatus, SUBCOLLECTIONS } from '@mitter-group/contracts';
import type {
  ICategory,
  IDriverPublicDocument,
  IStorefrontStatsDayDocument,
  IVenueDocument,
  IVenueTheme
} from '@mitter-group/contracts';
import { resolveTimeZone } from '@mitter-group/catalog';
import { IStoreDriver } from '../interfaces/driver.interface';
import { IStoreFood } from '../interfaces/food.interface';
import { IRejectReason, IStoreOrderDetail, IStoreOrderUpdate } from '../interfaces/order.interface';
import { IVenueReview } from '../interfaces/review.interface';
import { IStorefrontContact } from '../interfaces/storefront.interface';
import { readPhotos } from '../core/product-photos';

export class AuthInfo {
  constructor(public $uid: string) {}

  isLoggedIn() {
    return !!this.$uid;
  }
}

@Injectable({
  providedIn: 'root'
})
export class ApisService {
  static UNKNOWN_USER = new AuthInfo(null);
  db = firebase.firestore();
  public authInfo$: BehaviorSubject<AuthInfo> = new BehaviorSubject<AuthInfo>(ApisService.UNKNOWN_USER);
  constructor(
    private fireAuth: AngularFireAuth,
    //  Las referencias de AngularFire salen de `FirestoreRefs`, que las construye en un
    //  contexto de inyeccion: con `AngularFirestore` directo cada metodo de aca lanzaba
    //  `NG0203` en AngularFire 20 (ninguno corre en un constructor).
    private refs: FirestoreRefs,
    private http: HttpClient
  ) {}

  public checkAuth() {
    return new Promise((resolve) => {
      void this.fireAuth.onAuthStateChanged((user) => {
        resolve(user);
      });
    });
  }

  /**
   * Entra con correo y contraseña.
   *
   * **Un correo sin verificar no entra a la app** (NV-35, NV-51): la sesión de Auth queda
   * abierta, porque hace falta para reenviar el correo de verificación, pero no se escribe
   * nada en Firestore ni se avisa a la app (`authInfo$`). Resuelve igual con el usuario, y la
   * pantalla mira `emailVerified`. Un comercio ya registrado sin verificar entra por acá y
   * recibe el correo; no queda sin salida.
   *
   * Con el correo verificado se completa el perfil que falte (`ensureProfile`): quien creó la
   * cuenta y cerró la app antes de verificar no queda con una cuenta de Auth sin `users` ni
   * `venue`.
   */
  public login(email: string, password: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.fireAuth
        .signInWithEmailAndPassword(email, password)
        .then(async (res) => {
          if (res.user) {
            if (!(await this.refreshVerification(res.user))) {
              resolve(res.user);
              return;
            }
            this.signingOut = false;
            //  Una cuenta suspendida no escribe nada: ni perfil ni `fcm_token` (NV-31). Resuelve
            //  con el usuario y la pantalla la rechaza (`isAccountSuspended`).
            if (await this.isAccountSuspended(res.user.uid)) {
              resolve(res.user);
              return;
            }
            await this.ensureProfile(res.user);
            //  Solo si este dispositivo tiene suscripcion push. Sin ella (la web, hasta la
            //  fase 2) se escribia `''` y se borraba la de otro dispositivo del comercio.
            const subscriptionId = localStorage.getItem('fcm');
            if (subscriptionId) {
              void this.db.collection('users').doc(res.user.uid).update({ fcm_token: subscriptionId });
            }
            this.authInfo$.next(new AuthInfo(res.user.uid));
            resolve(res.user);
          }
        })
        .catch((err) => {
          this.authInfo$.next(ApisService.UNKNOWN_USER);
          reject(`login failed ${err}`);
        });
    });
  }

  /**
   * Relee la cuenta de Auth y dice si el correo ya está verificado. Si lo está, renueva el
   * token: la claim `email_verified` que miran las reglas viaja en él, y el token viejo
   * sigue diciendo `false` hasta que se renueva.
   */
  private async refreshVerification(user: firebase.User): Promise<boolean> {
    await user.reload();
    if (!user.emailVerified) {
      return false;
    }
    await user.getIdToken(true);
    return true;
  }

  getStatusActiveRef() {
    return this.refs.doc('statusProfile', '1').ref;
  }

  /**
   * Alta del comercio, en dos tiempos (NV-35, NV-51; decisión D3).
   *
   * Acá solo se crea la cuenta de Auth y se manda el correo de verificación: **no se escribe
   * `users` ni `venue`**. Las reglas exigen `email_verified` para crear `users/{uid}`, y un
   * correo sin verificar es el de cualquiera. El perfil lo crea `completeRegistration()` (o el
   * login) cuando el correo ya está verificado.
   *
   * Los nombres se guardan en `displayName` de la cuenta de Auth (viaja con ella a otro
   * dispositivo) y en `localStorage` (para separar nombre y apellido sin adivinar).
   */
  public register(emails: string, pwd: string, fnames: string, lnames: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.fireAuth
        .createUserWithEmailAndPassword(emails, pwd)
        .then(async (res) => {
          if (res.user) {
            const fullName = `${fnames ?? ''} ${lnames ?? ''}`.trim();
            localStorage.setItem(
              ApisService.PENDING_PROFILE_KEY,
              JSON.stringify({ uid: res.user.uid, fnames, lnames })
            );
            if (fullName) {
              await res.user.updateProfile({ displayName: fullName });
            }
            try {
              await res.user.sendEmailVerification();
            } catch (error) {
              //  La cuenta ya existe: reintentar el registro daria `email-already-in-use`. Se
              //  cierra la sesion y se manda al login, donde se reenvia el correo. Por `logout()`
              //  y no por `signOut()`: el observador de sesion toma un cierre no marcado por
              //  una suspension y avisaria «Cuenta suspendida» sobre este mensaje.
              log(error);
              await this.logout();
              reject(new Error(ApisService.VERIFICATION_EMAIL_FAILED));
              return;
            }
            resolve(res.user);
          }
        })
        .catch((err) => {
          this.authInfo$.next(ApisService.UNKNOWN_USER);
          reject(`login failed ${err}`);
        });
    });
  }

  /** Manda (o reenvía) el correo de verificación a la cuenta con la sesión abierta. */
  public async sendVerificationEmail(): Promise<void> {
    const user = await this.fireAuth.currentUser;
    if (!user) {
      throw new Error('No hay una sesión abierta');
    }
    await user.sendEmailVerification();
  }

  /**
   * «Ya verifiqué mi correo»: relee la cuenta y, si el correo está verificado, crea el perfil.
   * Resuelve con el uid, o `null` si todavía falta verificar.
   */
  public async completeRegistration(): Promise<string | null> {
    const user = await this.fireAuth.currentUser;
    if (!user || !(await this.refreshVerification(user))) {
      return null;
    }
    await this.ensureProfile(user);
    this.authInfo$.next(new AuthInfo(user.uid));
    return user.uid;
  }

  /** Error de `register()` cuando la cuenta se creó pero el correo no salió. */
  static readonly VERIFICATION_EMAIL_FAILED = 'verification-email-failed';

  /** Dónde se recuerdan los nombres tipeados en el registro hasta que se verifica el correo. */
  private static readonly PENDING_PROFILE_KEY = 'pendingProfile';

  /** Nombre y apellido del registro: lo recordado en este dispositivo o, si no, el `displayName`. */
  private namesOf(user: firebase.User): { fname: string; lname: string } {
    try {
      const pending = JSON.parse(localStorage.getItem(ApisService.PENDING_PROFILE_KEY) ?? 'null');
      if (pending && pending.uid === user.uid) {
        return { fname: pending.fnames ?? '', lname: pending.lnames ?? '' };
      }
    } catch {
      //  Un valor ilegible se ignora: se cae al `displayName`.
    }
    const [fname = '', ...rest] = (user.displayName ?? '').split(' ');
    return { fname, lname: rest.join(' ') };
  }

  /**
   * Crea `users/{uid}` si falta, **solo con el correo verificado**. Si ya existe (de este
   * comercio, o de un cliente o driver que entra por esta app) no se escribe nada: el login
   * rechaza después lo que no sea `type: 'venue'`.
   *
   * **No crea `venue/{uid}`**: nace donde siempre nació, en «Mi perfil» (`isNewVenue`), con el
   * redirect «Please create your venue profile first» de pedidos y cuenta. Sin `slug` ni
   * `statusProfile` (los escribe el admin, reglas de CA-6).
   *
   * **`status` de `users` es una referencia a `statusProfile`, no un string.** Antes se escribia
   * `'open'` (el horario de `venue`) y `readProfile()` hacia `data.status.get()` sobre un
   * string: el perfil del comercio no cargaba nunca. Ver el punto 16 de `docs/05-hallazgos.md`
   * de `mitter-packages`.
   */
  private async ensureProfile(user: firebase.User): Promise<void> {
    const userRef = this.db.collection(COLLECTIONS.USERS).doc(user.uid);
    if (!(await userRef.get()).exists) {
      const { fname, lname } = this.namesOf(user);
      await userRef.set({
        uid: user.uid,
        email: user.email ?? '',
        fname,
        lname,
        type: 'venue',
        status: this.getStatusActiveRef(),
        createdBy: 'self'
      });
    }
    localStorage.removeItem(ApisService.PENDING_PROFILE_KEY);
  }

  /** Verdadero mientras la app misma cierra la sesión: el observador no lo toma por una expulsión. */
  public signingOut = false;

  public logout(): Promise<void> {
    this.signingOut = true;
    this.authInfo$.next(ApisService.UNKNOWN_USER);
    // this.db.collection('users').doc(localStorage.getItem('uid')).update({ 'fcm_token': firebase.firestore.FieldValue.delete() })
    return this.fireAuth.signOut();
  }

  public resetPassword(email: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.fireAuth
        .sendPasswordResetEmail(email)
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(`reset failed ${err}`);
        });
    });
  }

  public checkEmail(email: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.fireAuth
        .fetchSignInMethodsForEmail(email)
        .then((info: any) => {
          resolve(info);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  public getProfile(id): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.refs
        .doc('users', id)
        .get()
        .subscribe(
          (profile: any) => {
            // El resultado del callback se encadena a resolve/reject. Antes el callback
            // era `async` y lo que lanzara adentro —empezando por el `data.status` de un
            // uid sin documento— quedaba en una promesa que nadie observaba: la promesa
            // de afuera no se resolvia **ni** se rechazaba y la pantalla se quedaba con
            // el spinner puesto. Es el mismo bug que mitter-driver-app documento como #3.
            void this.readProfile(profile).then(resolve, reject);
          },
          (error) => {
            reject(error);
          }
        );
    });
  }

  /**
   * Si la cuenta está suspendida (NV-31): `users/{uid}.status` o `venue/{uid}.statusProfile`
   * en `statusProfile/3`. Un comercio nuevo sin aprobar (sin `statusProfile` o inactivo) NO es
   * suspendido: entra a completar su perfil. Un error de Auth por cuenta deshabilitada o sesión
   * no renovable cuenta como suspendido; cualquier otro error (red, documento ausente) no
   * bloquea a nadie por sí solo.
   */
  public async isAccountSuspended(uid: string): Promise<boolean> {
    try {
      const profile = await this.getProfile(uid).catch((error) => {
        if (isAccountDisabledError(error)) {
          throw error;
        }
        return null;
      });
      if (profile && isSuspendedStatus(profile.status)) {
        return true;
      }
      const venue = await this.getVenueDetails(uid);
      return isSuspendedStatus(venue?.statusProfile);
    } catch (error) {
      return isAccountDisabledError(error);
    }
  }

  /** Lee el perfil y resuelve la referencia de `status` a su nombre. */
  private async readProfile(profile: any): Promise<any> {
    const data = profile.data();
    if (!data) {
      throw new Error(`No existe el perfil ${profile.id}`);
    }
    let status = null;
    if (typeof data.status === 'string') {
      // Documento legado: `status` es el string (`active`), no una referencia (NV-31).
      status = data.status;
    } else if (data.status) {
      const statusRef = await data.status.get();
      const res = statusRef.data();
      status = res && res.name ? res.name : null;
    }
    data.status = status;
    return data;
  }

  public getMessages(id): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.refs
        .collection(`messages/${id}/chats`)
        .get()
        .subscribe(
          (messages: any) => {
            log(messages);
            const data = messages.docs.map((element) => {
              const item = element.data() as any;
              item.id = element.id;
              return item;
            });
            resolve(data);
          },
          (error) => {
            reject(error);
          }
        );
    });
  }

  /**
   * Suma uno al contador de mensajes sin leerlo antes.
   *
   * `inbox.send()` hacia `{ count: this.count + 1 }`: leia el valor que tenia en pantalla,
   * le sumaba uno y lo escribia. Entre la lectura y la escritura puede entrar otro
   * mensaje —el del administrador, o el mismo comercio desde otro dispositivo— y esa
   * cuenta se pierde. `FieldValue.increment` deja la suma del lado del servidor, que es
   * atomica y no necesita conocer el valor previo.
   */
  public incrementMessageCount(uid: string): Promise<void> {
    return this.db
      .collection('users')
      .doc(uid)
      .update({ count: firebase.firestore.FieldValue.increment(1) });
  }

  public updateProfile(uid, param): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.db
        .collection('users')
        .doc(uid)
        .update(param)
        .then((data) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  public getVenueDetails(id: string): Promise<IVenueDocument | undefined> {
    return new Promise<IVenueDocument | undefined>((resolve, reject) => {
      this.refs
        .doc<IVenueDocument>(COLLECTIONS.VENUE, id)
        .get()
        .subscribe(
          (venue) => {
            resolve(venue.data());
          },
          (error) => {
            reject(error);
          }
        );
    });
  }

  /** El comercio en vivo: lo usa `TabsPage` para enterarse si el admin suspende delivery. */
  public watchVenue(id: string): Observable<IVenueDocument | undefined> {
    return this.refs.doc<IVenueDocument>(COLLECTIONS.VENUE, id).valueChanges();
  }

  public getVenueCategories(id): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.refs
        .collection('categories', (ref) => ref.where('uid', '==', id))
        .get()
        .subscribe(
          (venue) => {
            const data = venue.docs.map((element) => {
              const item = element.data() as IOrder;
              item.id = element.id;
              return item;
            });
            resolve(data);
          },
          (error) => {
            reject(error);
          }
        );
    });
  }

  public updateVenueCategoies(id, cid, param): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.refs
        .doc('categories', cid)
        .update(param)
        .then(
          (data) => {
            resolve(data);
          },
          (error) => {
            reject(error);
          }
        );
    });
  }

  public async getFoodWithId(uid: string, id: string): Promise<IStoreFood> {
    const data = await this.db
      .collection(COLLECTIONS.FOODS)
      .doc(uid)
      .collection(SUBCOLLECTIONS.FOODS_ALL)
      .doc(id)
      .get();
    const [plato] = await this.readFoods([data]);
    return plato;
  }

  public async getFoods(uid: string): Promise<IStoreFood[]> {
    const data = await this.db.collection(COLLECTIONS.FOODS).doc(uid).collection(SUBCOLLECTIONS.FOODS_ALL).get();
    return this.readFoods(data.docs);
  }

  /**
   * Resuelve la referencia `cid` de cada plato al documento de su categoria.
   *
   * Antes esto era `item.cid.get().then(doc => { item.cid = doc.data(); })` **sin
   * esperar**, y el `resolve` salia inmediatamente despues: la pantalla recibia los platos
   * con `cid` todavia siendo un `DocumentReference`, y se convertia en la categoria unos
   * milisegundos despues, in situ. Que a veces se viera bien era pura carrera.
   *
   * Es el mismo bug que este archivo ya corrigio en `readReviews()` para las resenas y que
   * se habia quedado sin corregir acá. Punto 25 de `docs/05-hallazgos.md` de
   * `mitter-packages`.
   *
   * Las lecturas van en paralelo con `Promise.all`; un plato cuya categoria ya no exista
   * deja `cid` en `null` en vez de tumbar la pantalla entera.
   */
  private async readFoods(docs: FoodSnapshot[]): Promise<IStoreFood[]> {
    return Promise.all(
      docs.map(async (element) => {
        const item = element.data() as Omit<IStoreFood, 'cid'> & { cid: unknown };
        item.id = element.id;
        //  `gallery` ausente es `[]`, y se lee con la misma desconfianza que la vitrina: lo que
        //  no sea una URL valida, las repetidas y la principal se descartan; sin principal, la
        //  primera pasa a serlo.
        Object.assign(item, readPhotos(item.cover, item.gallery));
        const referencia = item.cid as DocumentReference<ICategory> | null;
        if (referencia && typeof referencia.get === 'function') {
          try {
            const categoria = await referencia.get();
            item.cid = categoria.exists ? { ...categoria.data(), id: categoria.id } : null;
          } catch (err) {
            log('no se pudo leer la categoria del plato', item.id, err);
            item.cid = null;
          }
        }
        return item as IStoreFood;
      })
    );
  }

  public addFood(uid, id, param): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.refs
        .doc(`foods/${uid}/all`, id)
        .set(param)
        .then(
          (data) => {
            resolve(data);
          },
          (error) => {
            reject(error);
          }
        );
    });
  }

  public updateFood(uid, id, param): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.refs
        .doc(`foods/${uid}/all`, id)
        .update(param)
        .then(
          (data) => {
            resolve(data);
          },
          (error) => {
            reject(error);
          }
        );
    });
  }

  /**
   * Borra un producto (`foods/{uid}/all/{id}`). **No borra la foto**: el carrito y los pedidos
   * guardan su URL (`ICartItem.cover`) y la seguirían mostrando.
   */
  public deleteFood(uid: string, id: string): Promise<void> {
    return this.refs.doc(`foods/${uid}/all`, id).delete();
  }

  public addVenueCategoies(id, cid, param): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.refs
        .doc('categories', cid)
        .set(param)
        .then(
          (data) => {
            resolve(data);
          },
          (error) => {
            reject(error);
          }
        );
    });
  }

  /**
   * Guarda lo que el comercio edita de si mismo.
   *
   * **`merge` no es un detalle: sin el, este `set()` reemplaza el documento entero.** El
   * `informations` que llega son los dieciseis campos de la pantalla de perfil, y el
   * documento de `venue` tiene ademas los ~24 que carga el panel al dar de alta: banca,
   * ciudad, pais, los cuatro tipos de documento, `statusProfile`, `idPackage`,
   * `freeShipping`, `createdAt`. Sin `merge` todos esos se borraban la primera vez que el
   * comercio cambiaba una foto o un horario.
   *
   * Ver el punto 20 de `docs/05-hallazgos.md` de `mitter-packages`.
   */
  public createVenue(informations: any, backToReview = false): Promise<any> {
    if (backToReview) {
      //  `set(..., { merge: true })` mezcla los mapas y dejaría un `approved` viejo dentro de
      //  `channels.storefront`. `update` con la ruta con punto lo reemplaza (ver NV-54). El
      //  documento existe: solo se vuelve a revisión una vitrina que estaba activa.
      return this.db
        .collection(COLLECTIONS.VENUE)
        .doc(informations.uid)
        .update({ ...informations, ...this.storefrontBackToReview() });
    }
    return new Promise<any>((resolve, reject) => {
      this.refs
        .doc('venue', informations.uid)
        .set(informations, { merge: true })
        .then(
          (data) => {
            resolve(data);
          },
          (error) => {
            reject(error);
          }
        )
        .catch((error) => {
          reject(error);
        });
    });
  }

  //  --- La vitrina (fase 4.2c de `docs/VITRINA-WHATSAPP.md`)

  /**
   * El comercio pide un canal. Queda `pending` hasta que el admin lo decide desde el panel:
   * las reglas solo le dejan escribir eso, y con la hora del servidor.
   */
  /**
   * Guarda como le escriben los clientes al comercio: WhatsApp y Telegram. La direccion de la
   * vitrina y los canales ya no se escriben desde aca (22/10/2026): los decide el admin en el
   * panel, que reserva `slugs/{slug}` como hacia esta pantalla. Las reglas validan los dos
   * campos (`whatsappOk()`, `telegramOk()`).
   *
   * Un `telegram` vacio **borra** el campo con `FieldValue.delete()`: un `''` lo rechazarian
   * las reglas, y con el todo el `update`. `this.db` (el SDK `compat` directo) no tiene el
   * problema de `NG0203` de `FirestoreRefs`.
   */
  public saveStorefrontContact(uid: string, contact: IStorefrontContact, backToReview = false): Promise<void> {
    const { whatsapp, telegram } = contact;
    return this.db
      .collection(COLLECTIONS.VENUE)
      .doc(uid)
      .update({
        whatsapp,
        telegram: telegram ? telegram : firebase.firestore.FieldValue.delete(),
        ...(backToReview ? this.storefrontBackToReview() : {})
      });
  }

  /**
   * NV-54: el canal de la vitrina vuelve a `pending` **en la misma escritura** que cambia lo
   * publicado. La ruta con punto reemplaza el mapa `channels.storefront` entero (con `update`),
   * así que queda exactamente `{ status, updatedAt }`: las reglas rechazan cualquier otra
   * forma, y `approved` solo lo escribe el admin.
   */
  private storefrontBackToReview() {
    return {
      'channels.storefront': {
        status: 'pending',
        updatedAt: firebase.firestore.FieldValue.serverTimestamp()
      }
    };
  }

  /**
   * El interruptor del IVA de la vitrina (`venue.chargesVat`, `contracts@0.11.0`). Las reglas
   * exigen un booleano; la tasa es del país (`country.vat`) y la carga el panel.
   */
  public saveChargesVat(uid: string, chargesVat: boolean): Promise<void> {
    return this.db.collection(COLLECTIONS.VENUE).doc(uid).update({ chargesVat });
  }

  /**
   * Los colores de la vitrina (`venue.theme`, `contracts@0.12.0`). La pantalla ya comprobó el
   * contraste y el formato; las reglas (`themeOk()`) vuelven a validar el formato.
   */
  public saveStorefrontTheme(uid: string, theme: IVenueTheme): Promise<void> {
    return this.db.collection(COLLECTIONS.VENUE).doc(uid).update({ theme });
  }

  public updateVenue(informations: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.refs
        .doc('venue', informations.uid)
        .update(informations)
        .then(
          (data) => {
            resolve(data);
          },
          (error) => {
            reject(error);
          }
        )
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * INACTIVO A PROPOSITO. Devuelve un observable vacio sin pegarle a OneSignal.
   *
   * El envio de push necesita el REST Key, que es un **secreto de servidor**: el que
   * viajaba en `environment.restKey` esta en el bundle y en la historia de git de los
   * cuatro repos, y por eso se vacio (plan §3.9, P0). Con la clave vacia la cabecera
   * quedaba en `Basic ` y OneSignal contestaba 401 **en silencio**: el comercio aceptaba
   * un pedido creyendo que el cliente se enteraba, y no se enteraba. Es el bug #4 que
   * mitter-driver-app cerro igual.
   *
   * El envio va a una Cloud Function, que es la unica forma de tener la clave fuera del
   * dispositivo. Hasta entonces esto avisa por consola y no miente: devuelve un observable
   * que completa sin emitir. Por eso ningun llamador puede colgar de la respuesta el cierre
   * del spinner ni la navegacion: el detalle del pedido los cerraba ahi y el «Por favor
   * espera» quedaba puesto despues de aceptar o rechazar (hasta el 10/10/2026).
   */
  sendNotification(msg, title, id): Observable<unknown> {
    warn('push no enviada: el envio necesita una Cloud Function con el REST Key', { title, msg, id });
    return EMPTY;
  }

  public getMyOrders(id: string): Promise<IOrder[]> {
    return new Promise<IOrder[]>((resolve, reject) => {
      this.refs
        .collection('orders', (ref) => ref.where('venue.id', '==', id))
        .get()
        .subscribe(
          (venue) => {
            const data = venue.docs.map((element) => {
              const item = element.data() as any;
              /*
          item.uid.get().then(function (doc) {
            item.uid = doc.data();
            item.uid.id = doc.id;
          });
          */
              item.id = element.id;
              return item;
            });
            resolve(data);
          },
          (error) => {
            reject(error);
          }
        );
    });
  }

  public getOrderById(id: string): Promise<IStoreOrderDetail> {
    return new Promise<IStoreOrderDetail>((resolve, reject) => {
      this.refs
        .doc('orders', id)
        .get()
        .subscribe(
          (order: any) => {
            void this.readOrder(order).then(resolve, reject);
          },
          (error) => {
            reject(error);
          }
        );
    });
  }

  /**
   * Cambia `status` y, si se conoce, `statusId`: lo escriben tambien aceptar
   * (`OrderActionsService.accept`) y `rejectOrder()`, y el punto 28 de mitter-packages/docs/05-hallazgos.md documenta que
   * queda viejo justamente porque no todos los escritores lo actualizan. Si `statusOrder`
   * no tiene documento para el estado, se escribe solo `status` en vez de fallar.
   */
  public updateOrderStatus(id: string, value: OrderStatus, statusId?: string): Promise<void> {
    const param: IStoreOrderUpdate = statusId ? { status: value, statusId } : { status: value };
    return new Promise<void>((resolve, reject) => {
      this.refs
        .doc('orders', id)
        .update(param)
        .then(() => {
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Escribe un fragmento del pedido con `merge: true`, que es recursivo sobre mapas:
   * asignar (`OrderActionsService.assignDriver`) manda solo `delivery.driver` y no pisa el
   * resto de `delivery`; «Listo» manda parte de `preparation` y conserva el resto.
   * **No cambiar a `update()` sin pensarlo**: ahi si lo pisaria.
   */
  public updateOrder(id: string, param: IStoreOrderUpdate): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.refs
        .doc('orders', id)
        .set(param, { merge: true })
        .then((order: any) => {
          resolve(order);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Escribe un fragmento del pedido, con el mismo `merge` que `updateOrder()`, **solo si el
   * pedido sigue en `expected`**: lee y escribe en una transaccion. Si el pedido cambio entre
   * la lectura y el commit (el cliente cancelo, otra tablet rechazo), Firestore vuelve a
   * correr la funcion con el estado nuevo, y ese intento no escribe.
   *
   * Devuelve `false`, sin escribir, si el pedido no existe o ya no esta en `expected`. Va por
   * `this.db` (el SDK `compat` directo), que no tiene el problema de `NG0203` de `FirestoreRefs`.
   */
  public updateOrderIfStatus(id: string, expected: OrderStatus, param: IStoreOrderUpdate): Promise<boolean> {
    const ref = this.db.collection(COLLECTIONS.ORDERS).doc(id);
    return this.db.runTransaction(async (transaction) => {
      const snapshot = await transaction.get(ref);
      if (snapshot.data()?.status !== expected) {
        return false;
      }
      transaction.set(ref, param, { merge: true });
      return true;
    });
  }

  /**
   * Las subcategorias activas de una categoria de comercio (`storeCategory/{id}/subcategory`),
   * por nombre. La categoria la asigna el panel y el comercio la guarda por **nombre**
   * (`venue.storeCategory`), asi que se busca por `name`. Sin categoria, o si no existe,
   * devuelve `[]`.
   *
   * Es el catalogo que el panel administra en «Categorias de comercio». Reemplaza la lista
   * de cocinas cableada en el perfil (`cusine`), que el panel no conocia.
   */
  public async getSubcategoryNames(storeCategory: string): Promise<string[]> {
    if (!storeCategory) {
      return [];
    }
    const categories = await this.db
      .collection(COLLECTIONS.STORE_CATEGORY)
      .where('name', '==', storeCategory)
      .limit(1)
      .get();
    if (categories.empty) {
      return [];
    }
    const subcategories = await categories.docs[0].ref.collection('subcategory').get();
    return subcategories.docs
      .filter((d) => d.get('active') !== false)
      .map((d) => String(d.get('name') ?? ''))
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, 'es'));
  }

  /**
   * Los motivos de rechazo, de la coleccion compartida `rejectOrder`.
   *
   * **Es la misma lista que usa `mitter-panel`** (`services-order.service.ts#getReject`).
   * Antes esta app los tenia cableados en el HTML del modal, con valores propios
   * (`no_stock`, `no_pago`, `otro`) que el panel no podia ni mostrar ni traducir: usa el
   * `name` del documento como clave de traduccion y como valor de `reasonReject`.
   */
  public async getRejectReasons(audience: string): Promise<IRejectReason[]> {
    //  `audience` dice quien **elige** el motivo, no de quien es. La coleccion hace dos
    //  trabajos: es la lista para elegir y es tambien el registro de traducciones de todo
    //  valor que puede tener `reasonReject`, incluidos los que nadie elige nunca -- como
    //  el rechazo automatico que escribe la app de clientes. Esos van con `audience: []`
    //  y no los devuelve ninguna consulta.
    const snapshot = await this.db.collection('rejectOrder').where('audience', 'array-contains', audience).get();
    return snapshot.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<IRejectReason, 'id'>) }));
  }

  public getStatusOrder(name): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.refs
        .collection('statusOrder', (ref) => ref.where('name', '==', name))
        .get()
        .subscribe(
          (status: any) => {
            const data = status.docs.map((element) => {
              const item = element.data() as any;
              item.id = element.id;
              return item;
            });
            resolve(data);
          },
          (error) => {
            reject(error);
          }
        );
    });
  }

  defaultDataGetter(snapshot, collection = '') {
    const data = [];
    if (snapshot.empty) {
      log(`No matching ${collection} documents.`);
      return data;
    }

    snapshot.forEach((doc) => {
      const item = doc.data();
      item.id = doc.id;
      data.push(item);
    });
    return data;
  }

  /**
   * El codigo telefonico del pais del comercio (`venue.country` -> `country.phoneCode`), con su
   * `+`: `'+58'`, `'+54'`, `'+51'`. `''` si el comercio no tiene pais o el pais no tiene codigo:
   * no se supone Venezuela, porque un WhatsApp con el codigo equivocado manda los pedidos a otro
   * numero (hasta el 25/10/2026 la pantalla usaba `+58` fijo).
   */
  async getCountryPhoneCode(country: IVenueDocument['country']): Promise<string> {
    if (!country) {
      return '';
    }
    const data = (await this.getReferenceData(country)) as { phoneCode?: string | number } | null;
    const digits = String(data?.phoneCode ?? '').replace(/\D/g, '');
    return digits ? `+${digits}` : '';
  }

  /**
   * La moneda de cobro del pais del comercio (`venue.country` -> `country.priceCurrency`), **tal
   * cual venga**: la valida `parsePriceCurrency` (`core/price-currency.ts`). `undefined` si el
   * comercio no tiene pais o no se pudo leer; la pantalla usa entonces dolares.
   */
  async getCountryPriceCurrency(country: IVenueDocument['country']): Promise<unknown> {
    if (!country) {
      return undefined;
    }
    const data = (await this.getReferenceData(country)) as { priceCurrency?: unknown } | null;
    return data?.priceCurrency;
  }

  /**
   * La zona horaria del pais del comercio (`venue.country` -> `country.timeZone`), con la que
   * se corta el dia de las estadisticas de la vitrina. Sin pais, sin campo, con una zona que
   * `Intl` no conoce o si la lectura falla, la de Caracas (`resolveTimeZone`): nunca lanza.
   */
  async getCountryTimeZone(country: IVenueDocument['country']): Promise<string> {
    if (!country) {
      return resolveTimeZone(null);
    }
    const data = (await this.getReferenceData(country)) as { timeZone?: string | null } | null;
    return resolveTimeZone(data);
  }

  /**
   * Los dias de estadisticas de la vitrina del comercio entre `from` y `to` (`'YYYY-MM-DD'`,
   * ambos inclusive), ascendentes. Solo la subcoleccion del dueño: las reglas niegan el
   * `collectionGroup`, asi que nunca se consulta el grupo. Un `permission-denied` se propaga.
   */
  async getStorefrontStatsDays(uid: string, from: string, to: string): Promise<IStorefrontStatsDayDocument[]> {
    const snapshot = await this.db
      .collection(COLLECTIONS.STOREFRONT_STATS)
      .doc(uid)
      .collection(SUBCOLLECTIONS.STATS_DAYS)
      .where('date', '>=', from)
      .where('date', '<=', to)
      .orderBy('date')
      .get();
    return snapshot.docs.map((doc) => doc.data() as IStorefrontStatsDayDocument);
  }

  async getReferenceData(ref) {
    try {
      const refSnapshot = await ref.get();
      const data = await refSnapshot.data();
      return data;
    } catch (error) {
      log(error);
      return null;
    }
  }

  //  TODO: validar ciudad
  /**
   * Arma el pedido con el comercio ya resuelto y el cliente tal como viene en el pedido.
   *
   * **No lee `users/{cliente}`.** `users` lo leen solo su duenio y el admin (reglas del
   * 25/10/2026), y lo que esta app necesita del cliente ya lo copia en el pedido quien lo
   * crea (`order.user`: id, nombre, telefono, correo, foto y `fcmToken`, en
   * `confirm-order.page.ts` de `mitter-client-app`). Ninguna pantalla leia de `userData`
   * algo que `order.user` no tenga.
   */
  private async readOrder(order: { data(): IOrder }): Promise<IStoreOrderDetail> {
    const data = order.data();
    const venueSnapshot = await this.db.collection(COLLECTIONS.VENUE).doc(data.venue.id).get();
    const venueData = venueSnapshot.data() as any;
    venueData.id = venueSnapshot.id;
    const userData = { ...data.user };
    return { venueData, userData, ...data };
  }

  /**
   * Los drivers que el comercio puede asignar (modal transitorio de las fases 1 a 3).
   *
   * Lee `driverPublic`, no `users`: ahi el documento del driver trae `dni`, las fotos de la
   * cedula y la licencia y `numberBank`, y `users` lo leen solo su duenio y el admin.
   * `driverPublic` tiene solo drivers, asi que ya no se filtra por `type`.
   */
  public async getDrivers(): Promise<IStoreDriver[]> {
    const snapshot = await this.db.collection(COLLECTIONS.DRIVER_PUBLIC).get();
    const drivers: IStoreDriver[] = [];
    for (const doc of snapshot.docs) {
      const driver = doc.data() as IDriverPublicDocument;
      const status = await this.getReferenceData(driver.status);
      drivers.push({ ...driver, id: doc.id, status: get(status, 'name', null) });
    }
    return drivers;
  }

  /** La mitad publica del driver: nombre, foto, telefono, disponibilidad y posicion. */
  public getDriver(id: string): Promise<IDriverPublicDocument | undefined> {
    return new Promise<IDriverPublicDocument | undefined>((resolve, reject) => {
      this.refs
        .doc<IDriverPublicDocument>(COLLECTIONS.DRIVER_PUBLIC, id)
        .get()
        .subscribe(
          (driver) => {
            // `data()` es sincrono: el `await` que habia aca no esperaba nada.
            resolve(driver.data());
          },
          (error) => {
            reject(error);
          }
        );
    });
  }

  public getMyReviews(id: string): Promise<IVenueReview[]> {
    return new Promise<IVenueReview[]>((resolve, reject) => {
      this.refs
        .collection(COLLECTIONS.REVIEWS, (ref) => ref.where('restId', '==', id))
        .get()
        .subscribe(
          (review) => {
            resolve(this.readReviews(review.docs));
          },
          (error) => {
            reject(error);
          }
        );
    });
  }

  /**
   * Las resenas, con su id y **sin autor**.
   *
   * `uid` es una referencia a `users/{cliente}`, y desde el 25/10/2026 esa cuenta la leen
   * solo el cliente y el admin (reglas de Firestore): resolverla daba permission-denied en
   * cada resena, en cada carga. La resena no copia ni el nombre ni el correo del autor, asi
   * que la pantalla la muestra sin ellos. Copiar el nombre al escribirla es un cambio de
   * `mitter-client-app` y del contrato `IReview`.
   */
  private readReviews(docs: ReviewSnapshot[]): IVenueReview[] {
    return docs.map((element) => {
      const item = element.data() as Omit<IVenueReview, 'uid' | 'id'>;
      return { ...item, id: element.id, uid: null };
    });
  }
}
